import { useEffect, useState } from "react";
import client from "@/lib/api";
import { Panel } from "@/components/Layout";
import { KeyRound, ShieldCheck, Trash2, Lock } from "lucide-react";
import { toast } from "sonner";

export default function APIVault() {
  const [creds, setCreds] = useState([]);
  const [form, setForm] = useState({ carrier_name: "", api_key: "", api_secret: "", endpoint: "", notes: "" });

  const load = async () => {
    const { data } = await client.get("/vault/credentials");
    setCreds(data);
  };
  useEffect(() => { load(); }, []);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.carrier_name || !form.api_key) return toast.error("Carrier & API key required");
    try {
      await client.post("/vault/credentials", form);
      toast.success("Credential sealed in vault");
      setForm({ carrier_name: "", api_key: "", api_secret: "", endpoint: "", notes: "" });
      load();
    } catch {
      toast.error("Failed to seal credential");
    }
  };

  const remove = async (id) => {
    await client.delete(`/vault/credentials/${id}`);
    toast("Credential purged");
    load();
  };

  return (
    <div className="space-y-6" data-testid="vault-page">
      <header>
        <div className="label-tag mb-2">/// SECURITY — ENCRYPTED CARRIER VAULT</div>
        <h1 className="heading-display text-4xl sm:text-5xl">API Vault</h1>
        <p className="text-[#888] text-sm mt-2 max-w-2xl">
          Industrial-grade Fernet-encrypted storage for carrier credentials. Keys are masked at rest and never returned to the client in cleartext.
        </p>
      </header>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <SecurityBadge icon={Lock} label="Encryption" value="AES-256 / Fernet" />
        <SecurityBadge icon={ShieldCheck} label="Access" value="OMEGA-GOLD" />
        <SecurityBadge icon={KeyRound} label="Sealed Keys" value={creds.length} />
      </div>

      <Panel tag="// SEAL NEW" title="Add Carrier Credential" testId="vault-form-panel">
        <form onSubmit={submit} className="grid grid-cols-1 md:grid-cols-2 gap-3" data-testid="vault-form">
          <Field label="Carrier Name">
            <input required value={form.carrier_name} onChange={(e) => setForm({ ...form, carrier_name: e.target.value })} placeholder="e.g. Progressive, State Farm" className="hud-input" data-testid="vault-carrier" />
          </Field>
          <Field label="Endpoint (optional)">
            <input value={form.endpoint} onChange={(e) => setForm({ ...form, endpoint: e.target.value })} placeholder="https://api.carrier.com" className="hud-input" data-testid="vault-endpoint" />
          </Field>
          <Field label="API Key">
            <input required type="password" value={form.api_key} onChange={(e) => setForm({ ...form, api_key: e.target.value })} className="hud-input mono" data-testid="vault-key" />
          </Field>
          <Field label="API Secret (optional)">
            <input type="password" value={form.api_secret} onChange={(e) => setForm({ ...form, api_secret: e.target.value })} className="hud-input mono" data-testid="vault-secret" />
          </Field>
          <div className="md:col-span-2">
            <Field label="Notes">
              <input value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} className="hud-input" data-testid="vault-notes" />
            </Field>
          </div>
          <div className="md:col-span-2 flex justify-end">
            <button type="submit" className="btn-gold inline-flex items-center gap-2" data-testid="vault-submit">
              <Lock className="w-4 h-4" /> Seal Credential
            </button>
          </div>
        </form>
      </Panel>

      <Panel tag={`// VAULT — ${creds.length} SEALED`} title="Stored Credentials" testId="vault-list">
        <div className="space-y-2">
          {creds.map((c) => (
            <div key={c.id} className="grid grid-cols-12 items-center gap-3 p-3 border border-[#1a1a1a] hover:border-gold transition-colors" data-testid={`vault-row-${c.id}`}>
              <div className="col-span-12 md:col-span-3">
                <div className="font-bold uppercase tracking-wide">{c.carrier_name}</div>
                <div className="label-tag text-[#888]">{c.endpoint || "no endpoint"}</div>
              </div>
              <div className="col-span-12 md:col-span-5">
                <div className="label-tag mb-1">API Key (sealed)</div>
                <div className="mono text-sm bg-[#050505] border border-[#332A0C] px-3 py-2">{c.api_key_masked}</div>
              </div>
              <div className="col-span-10 md:col-span-3 text-[#888] text-xs">{c.notes || "—"}</div>
              <div className="col-span-2 md:col-span-1 flex justify-end">
                <button onClick={() => remove(c.id)} className="text-[#666] hover:text-[#FF3B30]" data-testid={`del-vault-${c.id}`}>
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
            </div>
          ))}
          {creds.length === 0 && <div className="text-center text-[#666] py-10">Vault is empty. Seal a carrier credential to begin.</div>}
        </div>
      </Panel>
    </div>
  );
}

function SecurityBadge({ icon: Icon, label, value }) {
  return (
    <div className="hud-panel p-5">
      <div className="hud-corner-tl" /><div className="hud-corner-tr" />
      <div className="hud-corner-bl" /><div className="hud-corner-br" />
      <div className="relative z-10 flex items-center justify-between">
        <div>
          <div className="label-tag">{label}</div>
          <div className="metric-value text-2xl mt-1 text-gold">{value}</div>
        </div>
        <Icon className="w-7 h-7 text-gold" />
      </div>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <div>
      <label className="label-tag block mb-1">{label}</label>
      {children}
    </div>
  );
}
